import React from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { useFetchDataId } from 'services/queries';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import SummaryHeader from '../sections/common/SummaryHeader';
import CustomsValueSection from '../sections/common/CustomsValueSection';
import CustomsTaxesSection from '../sections/common/CustomsTaxesSection';
import ProductTable from '../sections/common/ProductTable';
import DistributionExpensesSection from '../sections/distribution/DistributionExpensesSection';
import DistributionEntryInvoiceSection from '../sections/distribution/DistributionEntryInvoiceSection';
import DistributionSaleInvoiceSection from '../sections/distribution/DistributionSaleInvoiceSection';
import DistributionBuyerCostSection from '../sections/distribution/DistributionBuyerCostSection';
import NoTaxCalculationPrompt from '../components/NoTaxCalculationPrompt';

/**
 * DistributionView - Strategy component for DISTRIBUTION order type.
 * Mirrors the Capa Rel. Imp. workbook layout for distribution orders.
 * @param {Object} props - Component props
 * @param {Object} props.order - Order data
 */
const DistributionView = ({ order }) => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const { data: taxCalculation, error, isLoading } = useFetchDataId(
    'tax/order-tax-calculation/latest',
    id,
    true
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error loading tax calculation data. Please try again later.
      </Alert>
    );
  }

  if (taxCalculation && Object.keys(taxCalculation).length === 0) {
    return (
      <NoTaxCalculationPrompt
        order={order}
        onCalculationComplete={() => {
          queryClient.invalidateQueries(['tax/order-tax-calculation/latest', id]);
        }}
      />
    );
  }

  const combinedData = {
    ...order,
    latest_tax_calculation: taxCalculation
  };

  const exchangeRate = taxCalculation?.exchange_rate_dollar_to_real || 1;

  return (
    <Box p={2}>
      <Box className="print-section page-break-inside-avoid summary-grid">
        <SummaryHeader taxCalculation={taxCalculation} order={order} />
      </Box>
      <Box className="print-section page-break-inside-avoid">
        <CustomsValueSection order={combinedData} />
      </Box>
      <Box className="print-section page-break-inside-avoid">
        <CustomsTaxesSection order={combinedData} />
      </Box>
      <Box className="print-section page-break-inside-avoid">
        <DistributionExpensesSection order={combinedData} />
      </Box>
      <Box className="print-section page-break-inside-avoid">
        <DistributionEntryInvoiceSection order={combinedData} />
      </Box>
      <Box className="print-section page-break-inside-avoid">
        <DistributionSaleInvoiceSection order={combinedData} />
      </Box>
      <Box className="print-section page-break-inside-avoid">
        <DistributionBuyerCostSection order={combinedData} />
      </Box>
      <Box className="print-section page-break-inside-avoid">
        <ProductTable order={combinedData} exchangeRate={exchangeRate} />
      </Box>
    </Box>
  );
};

export default DistributionView;
